import React, { useState, useContext } from "react";
import axios from "../../axiosConfig";
import { OwnerContext } from "../../context/OwnerContext";
import { IoMdMale, IoMdFemale } from "react-icons/io";
import "./../../styles/OwnerAddDogModal.css";

const OwnerAddDogModal = ({ isOpen, onClose, onDogAdded }) => {
    const { owner } = useContext(OwnerContext);
    const [form, setForm] = useState({
        name: "",
        breed: "",
        dateOfBirth: "",
        approximate: false,
        gender: "",
        ownerId: owner ? owner.id : "",
    });
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleGenderSelect = (genderValue) => {
        setForm((prev) => ({
            ...prev,
            gender: genderValue,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name.trim()) {
            setErrors({ ...errors, name: "Imię pupila jest wymagane" });
            return;
        }
        if (!form.breed.trim()) {
            setErrors({ ...errors, breed: "Rasa jest wymagana" });
            return;
        }
        if (!form.dateOfBirth) {
            setErrors({ ...errors, dateOfBirth: "Data urodzenia jest wymagana" });
            return;
        }
        if (!form.gender) {
            setErrors({ ...errors, gender: "Płeć jest wymagana" });
            return;
        }

        try {
            const response = await axios.post("/api/v1/dog/add-dog", form);
            if (onDogAdded) {
                onDogAdded(response.data);
            }
            setForm({
                name: "",
                breed: "",
                dateOfBirth: "",
                approximate: false,
                gender: "",
                ownerId: owner ? owner.id : "",
            });
            setErrors({});
            onClose();
        } catch (error) {
            console.error("Error adding dog:", error);
            if (error.response && error.response.data) {
                setErrors(error.response.data);
            } else {
                setErrors({ general: "Błąd przy dodawaniu pupila. Spróbuj ponownie." });
            }
        }
    };

    const handleCancel = () => {
        setForm({
            name: "",
            breed: "",
            dateOfBirth: "",
            approximate: false,
            gender: "",
            ownerId: owner ? owner.id : "",
        });
        setErrors({});
        onClose();
    };

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    };

    return (
        <div className="addDogModal__overlay" onClick={handleOverlayClick}>
            <div className="addDogModal__content">
                <h2 className="addDogModal__title">Dodaj pupila</h2>
                <form className="addDogModal__form">
                    <div className="addDogModal__formGroup">
                        <label htmlFor="name" className="addDogModal__label">Imię psa</label>
                        <input
                            id="name"
                            type="text"
                            name="name"
                            className="addDogModal__input"
                            placeholder="Wpisz imię pupila"
                            value={form.name}
                            onChange={handleChange}
                            required
                        />
                        {errors.name && <span className="addDogModal__error">{errors.name}</span>}
                    </div>

                    <div className="addDogModal__formGroup">
                        <label htmlFor="breed" className="addDogModal__label">Rasa</label>
                        <input
                            id="breed"
                            type="text"
                            name="breed"
                            className="addDogModal__input"
                            placeholder="Wpisz rasę pupila"
                            value={form.breed}
                            onChange={handleChange}
                            required
                        />
                        {errors.breed && <span className="addDogModal__error">{errors.breed}</span>}
                    </div>

                    <div className="addDogModal__formGroup">
                        <label htmlFor="dateOfBirth" className="addDogModal__label">Data urodzenia</label>
                        <input
                            id="dateOfBirth"
                            type="date"
                            name="dateOfBirth"
                            className="addDogModal__input"
                            value={form.dateOfBirth}
                            onChange={handleChange}
                        />
                        {errors.dateOfBirth && <span className="addDogModal__error">{errors.dateOfBirth}</span>}
                    </div>

                    <div className="addDogModal__checkboxGroup">
                        <label htmlFor="approximate" className="addDogModal__checkboxLabel">
                            Przybliżony wiek
                            <input
                                id="approximate"
                                type="checkbox"
                                name="approximate"
                                className="addDogModal__checkbox"
                                checked={form.approximate}
                                onChange={handleChange}
                            />
                        </label>
                    </div>

                    <div className="addDogModal__formGroup">
                        <label className="addDogModal__label">Płeć</label>
                        <div className="addDogModal__genderSelector">
                            <span
                                className={`addDogModal__genderButton ${form.gender === "MALE" ? "addDogModal__genderButton--selected" : ""}`}
                                onClick={() => handleGenderSelect("MALE")}
                                role="button"
                                tabIndex="0"
                                aria-label="Samiec"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        handleGenderSelect("MALE");
                                    }
                                }}
                            >
                                <IoMdMale className="addDogModal__genderIcon" size={32} />
                                <span style={{ marginLeft: "8px" }}>Samiec</span>
                            </span>
                            <span
                                className={`addDogModal__genderButton ${form.gender === "FEMALE" ? "addDogModal__genderButton--selected" : ""}`}
                                onClick={() => handleGenderSelect("FEMALE")}
                                role="button"
                                tabIndex="0"
                                aria-label="Samica"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        handleGenderSelect("FEMALE");
                                    }
                                }}
                            >
                                <IoMdFemale className="addDogModal__genderIcon" size={32} />
                                <span style={{ marginLeft: "8px" }}>Samica</span>
                            </span>
                        </div>
                        {errors.gender && <span className="addDogModal__error">{errors.gender}</span>}
                    </div>

                    <input type="hidden" name="ownerId" value={form.ownerId} />

                    <div className="addDogModal__actions">
                        <button type="submit" className="addDogModal__submitButton" onClick={handleSubmit}>
                            Dodaj
                        </button>
                        <button type="button" className="addDogModal__cancelButton" onClick={handleCancel}>
                            Anuluj
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OwnerAddDogModal;